import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Radio, Loader2, Play, Square } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveStreamConfig = ({ pageId }) => {
  const [streamUrl, setStreamUrl] = useState('');
  const [streamActive, setStreamActive] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/pages/${pageId}`);
        setStreamUrl(res.data.live_stream_url || '');
        setStreamActive(res.data.live_stream_active || false);
        setSchedule(res.data.live_schedule || null);
      } catch (err) {
        console.error('Failed to load live stream config:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [pageId]);

  const goLive = async () => {
    if (!streamUrl.trim()) {
      toast.error('Enter a stream URL first');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, {
        live_stream_url: streamUrl,
        live_stream_active: true
      });
      setStreamActive(true);
      toast.success('Live stream is now active on all displays!');
    } catch (err) {
      toast.error('Failed to start live stream');
    } finally {
      setSaving(false);
    }
  };

  const endStream = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, {
        live_stream_active: false
      });
      setStreamActive(false);
      toast.success('Live stream ended. Displays returning to slideshow.');
    } catch (err) {
      toast.error('Failed to end stream');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const dayNames = { sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday' };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="live-stream-config">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Radio className={`w-5 h-5 ${streamActive ? 'text-red-500 animate-pulse' : 'text-cyan-400'}`} />
              Live Stream
              {streamActive && (
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-bold animate-pulse">
                  LIVE
                </span>
              )}
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              Override the slideshow with a live video stream
            </p>
          </div>
        </div>

        {/* Manual Go Live */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              type="text"
              value={streamUrl}
              onChange={(e) => setStreamUrl(e.target.value)}
              placeholder="YouTube Live URL or embed URL"
              className="bg-slate-950 border-slate-700 text-white flex-1"
              disabled={streamActive}
              data-testid="live-stream-url-input"
            />
            {!streamActive ? (
              <Button
                onClick={goLive}
                disabled={saving || !streamUrl.trim()}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="go-live-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-1" /> Go Live</>}
              </Button>
            ) : (
              <Button
                onClick={endStream}
                disabled={saving}
                variant="outline"
                className="border-red-500 text-red-400 hover:bg-red-500/10"
                data-testid="end-stream-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Square className="w-4 h-4 mr-1" /> End Stream</>}
              </Button>
            )}
          </div>
        </div>

        {/* Scheduled Stream Info */}
        {schedule && schedule.enabled && (
          <div className="mt-4 p-3 bg-slate-950 rounded-lg border border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
              <span className="text-emerald-400 text-sm font-medium">Scheduled Live Stream</span>
            </div>
            <div className="text-slate-300 text-sm">
              <p>
                <span className="text-slate-500">Channel:</span> {schedule.channel_name || 'YouTube Channel'}
              </p>
              <p>
                <span className="text-slate-500">Schedule:</span> Every {dayNames[schedule.day] || schedule.day}, {schedule.start_time} - {schedule.end_time} {schedule.timezone?.replace('America/', '').replace('_', ' ')}
              </p>
              <p className="text-slate-500 text-xs mt-1">
                Display will automatically switch to the live stream during this window.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LiveStreamConfig;
